import React, { useEffect, useMemo, useRef, useState } from 'react';

import { LuMessageCircle, LuShieldCheck, LuFocus, LuCompass } from 'react-icons/lu';



const EMPTY_TIMINGS = [];

const normalise = word => word.toLowerCase().replace(/[’‘]/g, "'").replace(/[^\p{L}\p{N}']/gu, '');

// Keep measured speech cues; estimate only script tokens absent from the ASR.

function buildTimeline(narration, wordTimings, duration) {

  const tokens = [...narration.matchAll(/(\S+)(\s*)/g)];

  const script = tokens.map(token=>token[1]);

  // Estimate speech and pauses separately when measured words are unavailable.

  // Paragraph breaks give each narrated detail time to remain highlighted.

  const speechSeconds = .4;

  const pauses = tokens.map(([_,word,space])=>

    (/[.!?]$/.test(word)?.35:/[,;:]$/.test(word)?.12:0) + (/\n\s*\n/.test(space)?.65:0));

  const weights = pauses.map(pause=>speechSeconds+pause);

  const spoken = (Array.isArray(wordTimings) ? wordTimings : []).filter(item =>

    item && typeof item.word === 'string' && Number.isFinite(item.start) && Number.isFinite(item.end) && item.start >= 0 && item.end >= item.start);

  const length = duration > 0 ? duration : spoken.length ? spoken[spoken.length - 1].end : weights.reduce((sum,weight)=>sum+weight,0);

  const mapped = Array(script.length).fill(null);

  if (spoken.length) {

    const rows = script.length + 1, columns = spoken.length + 1;

    const scores = new Uint16Array(rows * columns);

    const source = script.map(normalise), audio = spoken.map(item => normalise(item.word));

    for (let i = 1; i < rows; i += 1) for (let j = 1; j < columns; j += 1) {

      scores[i * columns + j] = source[i - 1] && source[i - 1] === audio[j - 1]

        ? scores[(i - 1) * columns + j - 1] + 1

        : Math.max(scores[(i - 1) * columns + j], scores[i * columns + j - 1]);

    }

    let i = script.length, j = spoken.length;

    while (i > 0 && j > 0) {

      if (source[i - 1] && source[i - 1] === audio[j - 1]) {

        mapped[i - 1] = { ...spoken[j - 1], measured: true }; i -= 1; j -= 1;

      } else if (scores[(i - 1) * columns + j] >= scores[i * columns + j - 1]) i -= 1;

      else j -= 1;

    }

  }

  const measured = mapped.filter(Boolean).length;

  for (let index = 0; index < mapped.length;) {

    if (mapped[index]) { index += 1; continue; }

    const from = index;

    while (index < mapped.length && !mapped[index]) index += 1;

    const start = from ? mapped[from - 1].end : 0;

    const end = Math.max(start, index < mapped.length ? mapped[index].start : length);

    const totalWeight = weights.slice(from,index).reduce((sum,weight)=>sum+weight,0);

    const scale = totalWeight ? (end-start)/totalWeight : 0;

    let cursor = start;

    for (let k = from; k < index; k += 1) {

      mapped[k] = {start:cursor,end:cursor+speechSeconds*scale,measured:false};

      cursor += weights[k]*scale;

    }

  }

  return { words: mapped, mode: measured === script.length ? 'measured' : measured ? 'mixed' : 'estimated' };

}

function wordOffset(narration, phrase) {

  const at = narration.indexOf(phrase);

  if (at < 0) throw new Error(`Missing narration cue: ${phrase}`);

  return narration.slice(0, at).trim().split(/\s+/).filter(Boolean).length;

}

function cueTime(narration, timeline, phrase) {

  return timeline.words[wordOffset(narration, phrase)]?.start ?? Infinity;

}

function activeIndex(items, now) {

  let index = 0;

  items.forEach((item, i) => { if (now >= item.start) index = i; });

  return index;

}

// Keep the same timing-input aliases and stable timeline as What Is Tribe.

function useNarrationTimeline(narration, wordTimings, duration) {

  const timingKey = JSON.stringify(Array.isArray(wordTimings)

    ? wordTimings.map(item => [item?.word, item?.start, item?.end]) : []);

  return useMemo(() => buildTimeline(narration, wordTimings, duration), [narration, duration, timingKey]);

}

// Read the audio clock each frame. Typing adds character boundaries so it

// stays in sync with speech, pause, seeking, and playback rate changes.

function useLessonClock({ time, audioElement, audioRef, playbackId, audioStatus } = {}, timeline) {

  const boundaries = useMemo(() => [...timeline.words.map(word => word.start), ...(timeline.animationBoundaries ?? [])].sort((a,b)=>a-b), [timeline]);

  const sourceTime = Number.isFinite(time) ? Math.max(0, time) : 0;

  const parentAnchor = useRef({ time: sourceTime, stamp: null });

  const controller = useRef({ time: sourceTime, boundary: -2, playing: false, rate: 1, syncId: 0 });

  const [clock, setClock] = useState({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1, syncId: 0, seeking: false });

  const boundaryAt = current => {

    let low = 0, high = boundaries.length;

    while (low < high) {

      const middle = (low + high) >>> 1;

      if (boundaries[middle] <= current + .005) low = middle + 1;

      else high = middle;

    }

    return low - 1;

  };

  useEffect(() => {

    const previous = parentAnchor.current;

    const stamp = performance.now();

    const elapsed = previous.stamp == null ? 0 : (stamp - previous.stamp) / 1000;

    const expected = previous.time + (controller.current.playing ? elapsed * controller.current.rate : 0);

    const seek = previous.stamp != null && Math.abs(sourceTime - expected) > .6;

    parentAnchor.current = { time: sourceTime, stamp };

    // The media element is authoritative when available; otherwise changes to

    // the parent's time keep the interpolated fallback clock anchored.

    if (!(audioElement ?? audioRef?.current) && seek) {

      controller.current.syncId += 1;

      controller.current.boundary = -2;

      controller.current.time = sourceTime;

      setClock({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1,

        syncId: controller.current.syncId, seeking: true });

    }

  }, [sourceTime, audioElement, audioRef]);

  useEffect(() => {

    const media = audioElement ?? audioRef?.current;

    const playbackChanged = controller.current.playbackId !== playbackId;

    if (!media) {

      const stamp = performance.now();

      const anchor = parentAnchor.current;

      const heldTime = anchor.time + (controller.current.playing && anchor.stamp != null

        ? (stamp-anchor.stamp)/1000*controller.current.rate : 0);

      // Anchor pause/resume to the elapsed clock instead of resetting to zero.

      parentAnchor.current = {time:playbackChanged?sourceTime:heldTime,stamp};

    }

    let frame;

    const read = () => {

      const state = controller.current;

      const playing = media ? !media.paused && !media.ended : audioStatus === 'Playing';

      const rate = media ? media.playbackRate || 1 : 1;

      const anchor = parentAnchor.current;

      const current = media ? Math.max(0, media.currentTime || 0) : Math.max(0,

        anchor.time + (playing && anchor.stamp != null ? (performance.now() - anchor.stamp) / 1000 * rate : 0));

      const boundary = boundaryAt(current);

      return { current, boundary, playing, rate };

    };

    const update = (force = false, seek = false) => {

      const sample = read(), state = controller.current;

      const rateChanged = sample.rate !== state.rate;

      if (seek || rateChanged) state.syncId += 1;

      state.time = sample.current;

      if (force || seek || rateChanged || sample.boundary !== state.boundary || sample.playing !== state.playing) {

        state.boundary = sample.boundary;

        state.playing = sample.playing;

        state.rate = sample.rate;

        setClock({ now: sample.current, playing: sample.playing, rate: sample.rate,

          syncId: state.syncId, seeking: seek || rateChanged });

      }

      return sample.playing;

    };

    const tick = () => { if (update()) frame = requestAnimationFrame(tick); };

    const play = () => { cancelAnimationFrame(frame); update(true); frame = requestAnimationFrame(tick); };

    const stop = () => { cancelAnimationFrame(frame); update(true); };

    const seek = () => { update(true, true); };

    const rate = () => { update(true); };

    const sync = () => { update(); };

    if (media) {

      media.addEventListener('play', play); media.addEventListener('pause', stop);

      media.addEventListener('ended', stop); media.addEventListener('seeked', seek);

      media.addEventListener('ratechange', rate); media.addEventListener('timeupdate', sync);

    }

    if (controller.current.playbackId !== playbackId) {

      controller.current.playbackId = playbackId;

      controller.current.syncId += 1;

    }

    update(true);

    if (read().playing) frame = requestAnimationFrame(tick);

    return () => {

      cancelAnimationFrame(frame);

      if (media) {

        media.removeEventListener('play', play); media.removeEventListener('pause', stop);

        media.removeEventListener('ended', stop); media.removeEventListener('seeked', seek);

        media.removeEventListener('ratechange', rate); media.removeEventListener('timeupdate', sync);

      }

    };

  }, [audioElement, audioRef, playbackId, audioStatus, boundaries]);

  return clock;

}

// Phrase-cued motion matches What Is Tribe and Event Structure. Delays stay

// fixed between clock updates, so CSS completes the gentle rise smoothly.

function PhraseReveal({ as: Tag = 'h1', text, emphasis = [], clock, complete = false, delay = 0 }) {

  const words = text.split(/\s+/).filter(Boolean);

  const settled = useMemo(() => clock.seeking || (!clock.playing && clock.now > 0), [clock.syncId, text]);

  const strong = new Set();

  emphasis.forEach(phrase => {

    const offset = wordOffset(text, phrase);

    phrase.split(/\s+/).forEach((_, index) => strong.add(offset + index));

  });

  return <Tag aria-label={text}>{words.map((word, index) => <React.Fragment key={index}>

    <span className={`authority-reveal-word ${strong.has(index) ? 'is-emphasized' : ''}`} aria-hidden="true" style={{

      animationDelay: `${complete || settled ? -10 : (delay + index * .095) / clock.rate}s`,

      animationDuration: `${.28 / clock.rate}s`,

      animationPlayState: complete || clock.playing ? 'running' : 'paused',

    }}>{word}</span>{index < words.length - 1 ? ' ' : ''}

  </React.Fragment>)}</Tag>;

}







const INTRO_SCENES = [

  { cue: 'The last concept', text: 'Use your authority.', emphasis: ['authority.'] },

  { cue: 'As a facilitator', text: 'Protect the event.', emphasis: ['Protect'] },

  { cue: 'That means setting boundaries', text: 'Guide people toward connection.', emphasis: ['connection.'] },

];



const AUTHORITY_POINTS = [

  { cue: 'For example', text: 'Step-In', detail: 'Address bad behavior when it happens.', Icon: LuMessageCircle },

  { cue: 'pulling them aside', text: 'Set Boundaries', detail: 'Be clear when someone crosses a line.', Icon: LuShieldCheck },

  { cue: 'reminding the group what they’re doing', text: 'Refocus', detail: 'Bring the group back to the activity.', Icon: LuFocus },

  { cue: 'Take ownership of your role', endCue: 'you have a responsibility to your guests to guide it.', text: 'Own your power', detail: 'Take responsibility for guiding your guests.', Icon: LuCompass },

];



function buildAuthorityCues(narration, timeline) {

  const intro = INTRO_SCENES.map(scene => ({ ...scene, start: cueTime(narration, timeline, scene.cue) }));

  const points = AUTHORITY_POINTS.map(point => ({ ...point, start: cueTime(narration, timeline, point.cue) }));

  const lastPhrase = AUTHORITY_POINTS.at(-1).endCue;

  const lastWord = wordOffset(narration, lastPhrase) + lastPhrase.split(/\s+/).length - 1;

  return { intro, points, listAt: points[0].start, equalAt: timeline.words[lastWord]?.end ?? Infinity };

}



function AuthorityPoints({ cues, clock, complete }) {

  const latest = complete ? cues.points.length - 1 : cues.points.reduce((index, point, i) => clock.now >= point.start ? i : index, -1);

  const equal = complete || clock.now >= cues.equalAt;

  return <ol className={`authority-points ${equal ? 'is-equal' : ''}`} aria-label="How to use your authority">

    {cues.points.map(({ Icon, ...point }, index) => {

      const visible = index <= latest;

      const focused = visible && !equal && index === latest;

      return <li key={point.cue} className={`authority-point ${index % 2 ? 'is-reversed' : ''} ${visible ? 'is-visible' : ''} ${focused ? 'is-focused' : ''} ${visible && !focused && !equal ? 'is-muted' : ''}`} aria-hidden={visible ? undefined : true} aria-current={focused ? 'step' : undefined}>

        <div className="authority-graphic" aria-hidden="true"><span className="authority-icon"><Icon /></span><span className="authority-connector" /></div>

        {visible && <div className="authority-point-copy" key={`${point.cue}-${clock.syncId}`}>

          <PhraseReveal as="h2" text={point.text} clock={clock} complete={complete} />

          <PhraseReveal as="p" text={point.detail} clock={clock} complete={complete} delay={.12} />

        </div>}

      </li>;

    })}

  </ol>;

}



// This lesson owns its visuals, narration metadata, and summary.

export default function UsingYourAuthority(props = {}) {

  const narration = UsingYourAuthority.parts[0].narration;

  const speechTimeline = useNarrationTimeline(narration, props.wordTimings ?? props.words ?? EMPTY_TIMINGS, props.duration ?? 0);

  const cues = useMemo(() => buildAuthorityCues(narration, speechTimeline), [narration, speechTimeline]);

  const timeline = useMemo(() => ({ ...speechTimeline, animationBoundaries: [cues.equalAt] }), [speechTimeline, cues]);

  const clock = useLessonClock({ ...props, playbackId: `${props.playbackId ?? 0}:authority` }, timeline);

  const complete = props.audioStatus === 'Complete' || Boolean((props.audioElement ?? props.audioRef?.current)?.ended);

  const listVisible = complete || clock.now >= cues.listAt;

  const caption = cues.intro[activeIndex(cues.intro, clock.now)];

  return <div className="host-background-slide host-authority-slide" aria-label={UsingYourAuthority.title}>

    <style>{AUTHORITY_STYLES}</style>

    <section className={`authority-scene ${listVisible ? 'is-list' : 'is-caption'}`} data-scene={listVisible ? 'points' : `intro-${activeIndex(cues.intro, clock.now)}`} data-timing-mode={timeline.mode}>

      <div className="authority-content">

        {listVisible ? <AuthorityPoints cues={cues} clock={clock} complete={complete} /> :

          <div className="authority-headline" key={`${caption.cue}-${clock.syncId}`}><PhraseReveal text={caption.text} emphasis={caption.emphasis} clock={clock} complete={complete} /></div>}

      </div>

    </section>

  </div>;

}



const AUTHORITY_STYLES = `

@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700;900&display=swap');

.host-authority-slide {width:100%;background:#fff;color:#111;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;-webkit-font-smoothing:antialiased;}

.host-authority-slide * {box-sizing:border-box;}

.host-authority-slide .authority-scene {display:flex;align-items:center;min-height:620px;padding:48px 44px;}

.host-authority-slide .authority-content {width:min(1000px,100%);margin:0 auto;}

.host-authority-slide .authority-headline {max-width:800px;margin:auto;text-align:center;}

.host-authority-slide .authority-headline h1 {margin:0;font-family:Merriweather,Georgia,serif;font-size:clamp(28px,3.2vw,40px);font-weight:700;line-height:1.25;letter-spacing:-.015em;}

.host-authority-slide .authority-points {display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:24px;max-width:820px;list-style:none;margin:0 auto;padding:0;}

.host-authority-slide .authority-point {display:grid;grid-template-rows:128px 126px;min-width:0;text-align:center;opacity:0;transition:opacity 300ms ease;}

.host-authority-slide .authority-point.is-visible {opacity:1;}

.host-authority-slide .authority-point.is-muted {opacity:.3;}

.host-authority-slide .authority-graphic {display:flex;flex-direction:column;align-items:center;padding-top:0;grid-row:1;grid-column:1;}

.host-authority-slide .authority-icon {position:relative;display:grid;place-items:center;width:76px;height:76px;flex:none;border:2px dotted #bdbdbd;border-radius:50%;transition:border-color 300ms ease;}

.host-authority-slide .authority-icon::before {content:'';position:absolute;inset:5px;border-radius:50%;background:#f5f5f5;}

.host-authority-slide .authority-icon svg {position:relative;width:28px;height:28px;}

.host-authority-slide .is-focused .authority-icon {border-color:#111;}

.host-authority-slide .authority-connector {position:relative;display:block;width:1px;height:42px;flex:none;background:#e4e4e4;}

.host-authority-slide .authority-connector::after {content:'';position:absolute;left:-3px;bottom:-3px;width:7px;height:7px;border:1.5px solid #e4e4e4;border-radius:50%;background:#fff;}

.host-authority-slide .authority-point-copy {grid-row:2;grid-column:1;max-width:190px;margin:0 auto;padding-top:4px;}

.host-authority-slide .authority-point-copy h2 {margin:0;font-family:Merriweather,Georgia,serif;font-size:18px;font-weight:700;line-height:1.35;letter-spacing:-.015em;}

.host-authority-slide .authority-point-copy p {margin:10px 0 0;color:#666;font-size:12.5px;font-weight:400;line-height:1.6;letter-spacing:-.01em;}

.host-authority-slide .authority-point.is-reversed .authority-point-copy {grid-row:1;align-self:end;padding:0 0 10px;}

.host-authority-slide .authority-point.is-reversed .authority-graphic {grid-row:2;flex-direction:column-reverse;justify-content:flex-end;padding-top:0;}

.host-authority-slide .authority-point.is-reversed .authority-connector::after {top:-3px;bottom:auto;}

.host-authority-slide .authority-reveal-word {display:inline-block;opacity:0;transform:translateY(.16em);animation:authority-word-in 280ms cubic-bezier(.18,.84,.22,1) both;}

.host-authority-slide .authority-reveal-word.is-emphasized {font-weight:900;}

@keyframes authority-word-in {to{opacity:1;transform:translateY(0);}}

.host-training .ht-lesson-layout:has(.host-authority-slide) {display:block;padding:0;}

.host-training .ht-lesson-layout:has(.host-authority-slide)>.ht-lesson-copy {display:none;}

.host-training .ht-lesson-visual:has(.host-authority-slide) {display:block;padding:0;background:#fff;border:0;border-radius:0;}

@media(max-width:800px){

 .host-authority-slide .authority-points {grid-template-columns:repeat(2,minmax(0,1fr));max-width:520px;gap:28px 36px;}

}

@media(max-width:700px){

 .host-authority-slide .authority-scene {min-height:530px;padding:32px 24px;}

 .host-authority-slide .authority-headline h1 {font-size:clamp(28px,7vw,34px);}

}

@media(max-width:580px){

 .host-authority-slide .authority-points {grid-template-columns:1fr;gap:26px;max-width:420px;}

 .host-authority-slide .authority-point {grid-template-columns:64px 1fr;grid-template-rows:auto;min-height:108px;gap:20px;text-align:left;}

 .host-authority-slide .authority-graphic,.host-authority-slide .authority-point.is-reversed .authority-graphic {grid-row:1;grid-column:1;display:block;padding-top:0;}

 .host-authority-slide .authority-icon {width:64px;height:64px;}

 .host-authority-slide .authority-icon svg {width:25px;height:25px;}

 .host-authority-slide .authority-connector {display:none;}

 .host-authority-slide .authority-point-copy,.host-authority-slide .authority-point.is-reversed .authority-point-copy {grid-row:1;grid-column:2;align-self:start;max-width:none;margin:0;padding:0;}

 .host-authority-slide .authority-point-copy h2 {font-size:18px;}

 .host-authority-slide .authority-point-copy p {font-size:12.5px;}

}

@media(prefers-reduced-motion:reduce){

 .host-authority-slide .authority-reveal-word {animation:none!important;opacity:1;transform:none;}

 .host-authority-slide .authority-point,.host-authority-slide .authority-icon {transition:none;}

}

`;



Object.assign(UsingYourAuthority, {

  "slide": 10,

  "id": "authority",

  "title": "Use Your Authority",

  "section": "Facilitation",

  "parts": [

    {

      "part": 0,

      "narration": "The last concept is using your authority. As a facilitator, part of your job is to protect the event. That means setting boundaries and steering people toward behaviors that are conducive to connection.\nFor example, when someone is talking over others, making off comments, or sidetracking the group, it’s your responsibility to step in and gently redirect.\nThat might mean pulling them aside for a quick check-in, reminding the group what they’re doing, or shifting the focus back to the room.\nTake ownership of your role. You’re not just there to watch the event unfold, you have a responsibility to your guests to guide it."

    }

  ],

  "summary": [

    "Step in. Set boundaries.",

    "Refocus the group. Own your power."

  ]

});
